"use client";

import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useJobAtsLink } from "../hooks/use-job-ats-link";
import { useAtsSyncRuns } from "../hooks/use-ats-sync-runs";
import { syncDirectionCopy } from "../lib/sync-direction-copy";
import type { SyncDirection } from "./sync-direction-icon";
import type { AtsSyncRun } from "../types";

function syncChip(run: AtsSyncRun | null, direction: SyncDirection | undefined) {
  const status = run?.status;
  const errors = run?.errors ?? 0;
  const copy = syncDirectionCopy(direction);
  if (status === "succeeded")
    return {
      label: copy.done,
      variant: "success" as const,
      // Sync runs per-connection (all linked jobs), so a record count here would
      // be connection-wide and misleading on a single job — the per-stage chips
      // already show this job's counts. Keep this to connection status only.
      tooltip:
        "This ATS connection's last sync completed cleanly. Candidates for this job appear in the stages below.",
    };
  if (status === "running")
    return {
      label: `${copy.inFlight}…`,
      variant: "info" as const,
      tooltip: "A sync is currently in progress.",
    };
  if (status === "failed")
    return {
      label: "Sync error",
      variant: "destructive" as const,
      tooltip: "The last sync failed to run. It will retry automatically.",
    };
  if (status === "partially_failed")
    return {
      label: "Sync issues",
      variant: "warning" as const,
      tooltip: `${errors} record${errors === 1 ? "" : "s"} failed to sync during the last run and will be retried on the next sync. The sync cursor is held so nothing is skipped.`,
    };
  return {
    label: copy.idle,
    variant: "muted" as const,
    tooltip: "No sync has completed yet for this connection.",
  };
}

/**
 * The job's ATS sync-status pill (Syncing… / Synced / …). Self-sufficient from
 * `jobId`: shows nothing unless the job is ATS-linked. Lives next to the job's
 * "Active" status badge in the job detail header.
 *
 * While the job's initial ATS import runs, `importing` forces "Syncing…" —
 * the connection-scoped `syncChip` can otherwise read a stale "Synced" from a
 * sibling job's completed run the moment this job links.
 */
export function JobSyncChip({ jobId }: { jobId: string }) {
  const { isLinked, connectionId, connectorName, importing, syncDirection } =
    useJobAtsLink(jobId);
  const { latest } = useAtsSyncRuns(isLinked ? connectionId : null);

  if (!isLinked) return null;

  const copy = syncDirectionCopy(syncDirection);
  const chip = importing
    ? {
        label: `${copy.inFlight}…`,
        variant: "info" as const,
        tooltip: `Importing candidates from ${connectorName || "this ATS"}…`,
      }
    : syncChip(latest ?? null, syncDirection);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Badge variant={chip.variant} size="xs" className="rounded-md">
            {chip.label}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px]">{chip.tooltip}</TooltipContent>
    </Tooltip>
  );
}
